import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { aiDashboardKeys } from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import StyledDataGrid from "../../../../../components/StyledDataGrid";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { LATEST } from "../../../../../utils/constants";
import { getPrettyDate, getReadableTime } from "../../../../../utils/timestamp";
import { AIIntegrationsIcons } from "../../../../stack/environment/icons/iconExport";
import WidgetWrapper from "../../../custom_dashboard/WidgetWrapper";

const AI_APP_DISPLAY_NAMES = {
  chatgpt: "ChatGPT",
  cursor: "Cursor",
  claude: "Claude",
  gemini: "Gemini",
  deepseek: "DeepSeek",
  copilot: "GitHub Copilot",
};

const AIAppUsageWidget = () => {
  const colors = useColors();
  const { getAIAppUsageTrend } = useAPI();

  const { data: aiAppUsageTrend, isLoading } = useQuery({
    queryKey: aiDashboardKeys.widgetType("aiAppUsage"),
    queryFn: getAIAppUsageTrend,
    select: (data) => data.data,
  });

  const currentTimePoint =
    !aiAppUsageTrend || Object.keys(aiAppUsageTrend).length === 0
      ? null
      : aiAppUsageTrend[LATEST];

  const rows =
    !currentTimePoint || typeof currentTimePoint !== "object"
      ? []
      : Object.entries(currentTimePoint).map(([appKey, appData]) => ({
          id: appKey,
          app_key: appKey,
          display_name: AI_APP_DISPLAY_NAMES[appKey] || appData.display_name,
          users: appData.user_ids?.length || 0,
          last_access: appData.last_access,
        }));

  const columns = [
    {
      field: "app_key",
      headerName: "App",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const appIcon = AIIntegrationsIcons[params.value];
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {appIcon && (
              <SvgToIcon IconLogo={appIcon} width="24px" height="24px" />
            )}

            <Typography variant="caption" color={colors.text_primary}>
              {params.row.display_name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "users",
      headerName: "Users",
      flex: 0.6,
      minWidth: 100,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Typography variant="body2" color={colors.text_primary}>
          {params.value.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: "last_access",
      headerName: "Last Access",
      flex: 0.8,
      minWidth: 150,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Typography variant="caption" color={colors.grey_700}>
          {params.value
            ? `${getPrettyDate(params.value, true)}, ${getReadableTime(
                params.value,
                true
              )}`
            : "-"}
        </Typography>
      ),
    },
  ];

  return (
    <WidgetWrapper title="AI App usage" info="applications by user adoption">
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <LoadingIcon size={12} />
        </Box>
      ) : !rows || rows.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary">
            No AI app usage data found
          </Typography>
        </Box>
      ) : (
        <Box sx={{ height: 400 }}>
          <StyledDataGrid
            columns={columns}
            rows={rows}
            loading={isLoading}
            getRowId={(row) => row.id}
            initialState={{
              sorting: {
                sortModel: [{ field: "users", sort: "desc" }],
              },
              pagination: {
                paginationModel: { pageSize: 7 },
              },
            }}
            pageSizeOptionsOverride={[7, 10]}
            checkboxSelection={false}
            disableRowSelectionOnClick
            boxSXProps={{
              width: "100%",
              border: "none",
              ".MuiDataGrid-root": {
                border: "none",
                boxShadow: "none",
                "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
                  {
                    paddingLeft: "16px",
                  },
                "& .MuiDataGrid-cell:last-of-type, & .MuiDataGrid-columnHeader:last-of-type":
                  {
                    paddingRight: "16px",
                  },
                "& .MuiDataGrid-virtualScroller": {
                  minHeight: "270px",
                },
              },
            }}
          />
        </Box>
      )}
    </WidgetWrapper>
  );
};

export default AIAppUsageWidget;
